/*
 * Copyright (c) 2022 SARL SIGMA POLYNESIA
 */

import { useCallback, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import ListCard from '../../../../client/js/components/ListCard'
import DeleteSelectionModal from '../../../../client/js/components/modals/DeleteSelectionModal'
import PageTitle from '../../../../client/js/components/PageTitle'
import SelectionAlert from '../../../../client/js/components/selection/SelectionAlert'
import useSelection from '../../../../client/js/components/selection/useSelection'
import useQueryParams, { QueryParams } from '../../../../client/js/hooks/useQueryParams'
import { deleteTransaction, findManyTransactions } from '../../lib/rest/transactionAPIClient'
import TransactionList from './TransactionList'
import { useQuery } from '@tanstack/react-query'
import { getHalEmbedded, getHalPageTotalElements } from '@jalik/hal'
import LoadingHelper from '../../../../client/js/components/LoadingHelper'
import usePaginationQuery from '../../../../client/js/components/pagination/usePaginationQuery'
import RequirePerm from '../../../auth/components/RequirePerm'
import SearchForm from '../../../../client/js/components/search/SearchForm'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSyncAlt } from '@fortawesome/free-solid-svg-icons/faSyncAlt'
import { faFilter } from '@fortawesome/free-solid-svg-icons/faFilter'
import { faTrash } from '@fortawesome/free-solid-svg-icons/faTrash'
import { Button, Container } from '@mantine/core'
import AnchorLink from '../../../../client/js/components/AnchorLink'
import Toolbar from '../../../../client/js/components/Toolbar'
import TransactionStatusFilter from '../filters/TransactionStatusFilter'
import TransactionCategoryFilter from '../filters/TransactionCategoryFilter'
import GeneratedFilter from '../filters/GeneratedFilter'
import WithInvoiceFilter from '../filters/WithInvoiceFilter'
import Filters from '../../../../client/js/components/Filters'
import Page from '../../../../client/js/components/Page'

export type TransactionListQuery = {
  limit?: string,
  page?: string,
  search?: string,
  status?: string[],
  category?: string[],
  generated?: string,
  withInvoice?: string,
  expense?: string,
}

const initialQuery: TransactionListQuery = {
  limit: '10',
  page: '1'
}

export type TransactionListPageProps = {
  expense: boolean
  createLink?: string,
  editLink?: string,
}

function TransactionListPage (props: TransactionListPageProps) {
  const {
    createLink = 'create',
    editLink = '{id}/edit',
    expense
  } = props
  const intl = useIntl()

  const title = expense
    ? intl.formatMessage({
      id: 'expenses',
      defaultMessage: 'Dépenses'
    })
    : intl.formatMessage({
      id: 'revenues',
      defaultMessage: 'Revenus'
    })

  const createLabel = expense
    ? intl.formatMessage({
      id: 'create_expense',
      defaultMessage: 'Créer une dépense'
    })
    : intl.formatMessage({
      id: 'create_revenue',
      defaultMessage: 'Créer un revenu'
    })

  const [searchParams, setSearchParams] = useQueryParams()
  const [totalElements, setTotalElements] = useState<number>(0)
  const pagination = usePaginationQuery(totalElements)
  const { page, limit } = pagination

  const queryParams = useMemo(() => {
    return {
      ...initialQuery,
      ...searchParams,
      expense,
      page,
      limit
    }
  }, [expense, limit, page, searchParams])

  const listQuery = useQuery({
    enabled: true,
    queryKey: ['transactions', queryParams],
    queryFn: () => findManyTransactions(queryParams).then((r) => {
      setTotalElements(getHalPageTotalElements(r))
      return r
    })
  })
  const items: Transaction[] = useMemo(() => (
    (listQuery.data
      ? getHalEmbedded(listQuery.data)?.transactions
      : null) || []
  ), [listQuery.data])

  const setFilter = useCallback((name: string, value?: unknown) => {
    setSearchParams((s: QueryParams) => ({ ...s, page: '1', [name]: String(value) }))
  }, [setSearchParams])

  const setSearch = useCallback((search: string) => {
    setFilter('search', search)
  }, [setFilter])

  // Items selection.
  const [selectedItems, setSelectedItems] = useState<number[]>([])
  const selection = useSelection<number>({
    multiple: true,
    items: selectedItems,
    onChange: setSelectedItems,
    source: items.map((el) => el.id)
  })

  // Items deletion.
  const handleDelete = useCallback((id: number) => deleteTransaction(id), [])
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const toggleDeleteModal = useCallback(() => {
    setIsDeleteModalOpen((s) => !s)
  }, [])

  // Items filtering.
  const [isFiltersVisible, setIsFiltersVisible] = useState(true)
  const toggleFilters = useCallback(() => {
    setIsFiltersVisible((s) => !s)
  }, [])

  return (
    <Page>
      <Container size="xl">
        <PageTitle
          hidden
          title={title}
        />

        <ListCard
          filters={<Filters in={isFiltersVisible}>
            <TransactionStatusFilter
              onChange={setFilter}
              value={queryParams.status}
            />
            <TransactionCategoryFilter
              onChange={setFilter}
              value={queryParams.category}
            />
            <GeneratedFilter
              onChange={setFilter}
              value={queryParams.generated}
            />
            <WithInvoiceFilter
              onChange={setFilter}
              value={queryParams.withInvoice}
            />
          </Filters>}
          pagination={pagination}
          selection={selection}
          selectionActions={<SelectionAlert>
            <RequirePerm
              permissions={['write:transaction', 'write:invoice']}
              render={() => (
                <Button onClick={toggleDeleteModal}>
                  <FontAwesomeIcon
                    fixedWidth
                    icon={faTrash}
                  />
                </Button>
              )}
            />
          </SelectionAlert>}
          toolbar={<Toolbar>
            <RequirePerm
              permissions={['write:transaction', 'write:invoice']}
              render={() => (
                <Button
                  variant="filled"
                  component={AnchorLink}
                  to={createLink}
                >
                  {createLabel || intl.formatMessage({
                    id: 'create_transaction',
                    defaultMessage: 'Créer une transaction'
                  })}
                </Button>
              )}
            />
            <SearchForm
              initialValue={queryParams.search}
              onSubmit={setSearch}
              submitting={listQuery.isFetching}
            />
            <Button
              loading={listQuery.isFetching}
              onClick={() => {
                listQuery.refetch()
              }}
            >
              <FontAwesomeIcon
                fixedWidth
                icon={faSyncAlt}
              />
            </Button>
            <Button
              // todo active={isFiltersVisible}
              onClick={toggleFilters}
            >
              <FontAwesomeIcon
                fixedWidth
                icon={faFilter}
              />
            </Button>
          </Toolbar>}
        >
          <LoadingHelper
            query={listQuery}
            render={() => (
              <TransactionList
                itemLink={editLink}
                items={items}
              />
            )}
          />
        </ListCard>
      </Container>

      {isDeleteModalOpen
        ? <DeleteSelectionModal
          opened={isDeleteModalOpen}
          items={selection.items}
          onClose={toggleDeleteModal}
          onConfirm={handleDelete}
          onSuccess={() => {
            selection.clear()
            listQuery.refetch()
          }}
          requirePin
        />
        : null}
    </Page>
  )
}

export default TransactionListPage
